import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import * as THREE from 'three';

const t = {
  ar: {
    who: 'من أنت؟',
    guardian_title: 'ولي الأمر',
    guardian_sub: 'متابعة أداء أبنائي',
    student_title: 'الطالب',
    student_sub: 'هيا نتعلم!',
    back: '→ العودة للصفحة الرئيسية',
  },
  en: {
    who: 'Who are you?',
    guardian_title: 'Guardian',
    guardian_sub: "Monitor my children's progress",
    student_title: 'Student',
    student_sub: "Let's learn!",
    back: '→ Back to Home',
  },
};

export default function ChooseRole() {
  const navigate = useNavigate();
  const { lang, setLang } = useLanguage();
  const dir = lang === 'ar' ? 'rtl' : 'ltr';
  const T = t[lang];

  const [guardianHover, setGuardianHover] = useState(false);
  const [studentHover,  setStudentHover]  = useState(false);

  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(55, window.innerWidth / window.innerHeight, 0.1, 100);
    camera.position.z = 7;
    scene.add(new THREE.AmbientLight(0xffffff, 0.4));
    const L1 = new THREE.DirectionalLight(0xffffff, 1.2); L1.position.set(6, 8, 4); scene.add(L1);
    const L2 = new THREE.DirectionalLight(0xffffff, 0.6); L2.position.set(-5, -3, 4); scene.add(L2);
    const meshes = [];
    [
      [new THREE.IcosahedronGeometry(0.36, 0), -4, 2.5, -1.2],
      [new THREE.IcosahedronGeometry(0.24, 0), 3.6, 2, -2],
      [new THREE.OctahedronGeometry(0.30, 0), -3, -1.8, -1.5],
      [new THREE.OctahedronGeometry(0.20, 0), 2.8, -2.2, -1],
      [new THREE.TetrahedronGeometry(0.26, 0), 0.7, 2.8, -1.8],
      [new THREE.TetrahedronGeometry(0.16, 0), -1.4, -2.6, -1.4],
      [new THREE.IcosahedronGeometry(0.14, 0), 4, 0.5, -0.6],
      [new THREE.OctahedronGeometry(0.12, 0), -4, -0.7, -0.6],
      [new THREE.IcosahedronGeometry(0.11, 0), 1.8, -3, -0.9],
      [new THREE.TetrahedronGeometry(0.13, 0), -2, 3, -0.9],
    ].forEach(([G, x, y, z]) => {
      const mat = new THREE.MeshPhongMaterial({ color: 0x34D399, transparent: true, opacity: 0.26, shininess: 120, specular: new THREE.Color(0xffffff) });
      const m = new THREE.Mesh(G, mat);
      m.position.set(x, y, z);
      m.rotation.set(Math.random() * Math.PI * 2, Math.random() * Math.PI * 2, Math.random() * Math.PI * 2);
      m._oy = y; m._fs = 0.2 + Math.random() * 0.5; m._fa = 0.12 + Math.random() * 0.22;
      m._rx = (0.002 + Math.random() * 0.006) * (Math.random() > 0.5 ? 1 : -1);
      m._ry = (0.002 + Math.random() * 0.005) * (Math.random() > 0.5 ? 1 : -1);
      m._ph = Math.random() * Math.PI * 2;
      scene.add(m); meshes.push(m);
    });
    const PC = 120;
    const pp = new Float32Array(PC * 3);
    const pv = [];
    for (let i = 0; i < PC; i++) {
      pp[i * 3] = (Math.random() - 0.5) * 16;
      pp[i * 3 + 1] = (Math.random() - 0.5) * 12;
      pp[i * 3 + 2] = (Math.random() - 0.5) * 5 - 1.5;
      pv.push({ vx: (Math.random() - 0.5) * 0.007, vy: (Math.random() - 0.5) * 0.007 });
    }
    const pGeo = new THREE.BufferGeometry();
    pGeo.setAttribute('position', new THREE.BufferAttribute(pp, 3));
    const pMat = new THREE.PointsMaterial({ color: 0x34D399, size: 0.07, transparent: true, opacity: 0.5, sizeAttenuation: true });
    scene.add(new THREE.Points(pGeo, pMat));
    const lp = new Float32Array(PC * PC * 6);
    const lGeo = new THREE.BufferGeometry();
    lGeo.setAttribute('position', new THREE.BufferAttribute(lp, 3));
    const lMesh = new THREE.LineSegments(lGeo, new THREE.LineBasicMaterial({ color: 0x34D399, transparent: true, opacity: 0.1 }));
    scene.add(lMesh);
    let mx = 0, my = 0;
    const onMM = (e) => { mx = (e.clientX / window.innerWidth - 0.5) * 2; my = (e.clientY / window.innerHeight - 0.5) * 2; };
    window.addEventListener('mousemove', onMM);
    const onResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', onResize);
    let t = 0, li = 0, animId;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      t += 0.007;
      meshes.forEach(m => {
        m.rotation.x += m._rx; m.rotation.y += m._ry;
        m.position.y = m._oy + Math.sin(t * m._fs + m._ph) * m._fa;
      });
      for (let i = 0; i < PC; i++) {
        pp[i * 3] += pv[i].vx; pp[i * 3 + 1] += pv[i].vy;
        if (Math.abs(pp[i * 3]) > 8) pv[i].vx *= -1;
        if (Math.abs(pp[i * 3 + 1]) > 6) pv[i].vy *= -1;
      }
      pGeo.attributes.position.needsUpdate = true;
      li = 0;
      for (let i = 0; i < PC; i++) for (let j = i + 1; j < PC; j++) {
        const dx = pp[i * 3] - pp[j * 3], dy = pp[i * 3 + 1] - pp[j * 3 + 1];
        if (dx * dx + dy * dy < 5) {
          lp[li++] = pp[i * 3]; lp[li++] = pp[i * 3 + 1]; lp[li++] = pp[i * 3 + 2];
          lp[li++] = pp[j * 3]; lp[li++] = pp[j * 3 + 1]; lp[li++] = pp[j * 3 + 2];
        }
      }
      lGeo.attributes.position.needsUpdate = true;
      lGeo.setDrawRange(0, li / 3);
      camera.position.x += (mx * 0.3 - camera.position.x) * 0.032;
      camera.position.y += (-my * 0.18 - camera.position.y) * 0.032;
      camera.lookAt(0, 0, 0);
      renderer.render(scene, camera);
    };
    animate();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('mousemove', onMM);
      window.removeEventListener('resize', onResize);
      renderer.dispose();
    };
  }, []);

  const cardBase = {
    background: 'rgba(255,255,255,.72)',
    backdropFilter: 'blur(32px) saturate(2.2)',
    WebkitBackdropFilter: 'blur(32px) saturate(2.2)',
    border: '1px solid rgba(255,255,255,.92)',
    borderRadius: 24,
    boxShadow: '0 8px 48px rgba(0,0,0,.07), 0 1px 0 rgba(255,255,255,.98) inset',
    padding: '36px 28px',
    width: 220,
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'transform .15s, box-shadow .15s, border-color .15s',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    userSelect: 'none',
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'transparent',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: "'Nunito', system-ui, sans-serif",
      direction: dir,
      position: 'relative',
      zIndex: 10,
      padding: '40px 20px',
    }}>
      <canvas ref={canvasRef} style={{position:'fixed',inset:0,zIndex:0,pointerEvents:'none'}} />
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700;800&family=Nunito:wght@400;500;600;700&display=swap');
        @media (max-width: 600px) { .choose-cards { flex-direction: column !important; align-items: center; } }
        @media (min-width: 601px) and (max-width: 1023px) { .choose-cards { gap: 24px !important; } }
        @media (min-width: 1024px) { .choose-cards { gap: 32px !important; } }
        * { box-sizing: border-box; }
      `}</style>

      <div style={{ position: 'absolute', top: 20, left: 20, zIndex: 10 }}>
        <button
          onClick={() => setLang(lang === 'ar' ? 'en' : 'ar')}
          style={{
            background: 'white',
            border: '2px solid #E5E7EB',
            color: '#059669',
            fontWeight: 700,
            fontSize: '0.8rem',
            padding: '4px 12px',
            borderRadius: 20,
            cursor: 'pointer',
            fontFamily: 'inherit',
            boxShadow: '0 3px 0 #E5E7EB',
            transition: 'transform .08s, box-shadow .08s',
          }}
        >
          {lang === 'ar' ? 'EN' : 'ع'}
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 8 }}>
        <img
          src="/jahbath-hat.png"
          alt="Jahbath"
          style={{ height: 70, objectFit: 'contain', marginBottom: 10 }}
        />
        <div style={{ fontFamily: "'Sora', sans-serif", fontWeight: 700, fontSize: '2rem', color: '#064E3B' }}>
          Jah<span style={{ color: '#059669' }}>bath</span>
        </div>
      </div>

      <h1 style={{
        fontFamily: "'Sora', sans-serif",
        fontSize: '1.4rem',
        color: '#064E3B',
        marginTop: 8,
        marginBottom: 40,
        fontWeight: 700,
      }}>
        {T.who}
      </h1>

      <div className="choose-cards" style={{ display: 'flex', gap: 24 }}>
        <div
          onClick={() => navigate('/guardian/auth')}
          onMouseEnter={() => setGuardianHover(true)}
          onMouseLeave={() => setGuardianHover(false)}
          onMouseDown={e => { e.currentTarget.style.transform = 'translateY(3px)'; e.currentTarget.style.boxShadow = '0 1px 0 #E5E7EB'; }}
          onMouseUp={e => { e.currentTarget.style.transform = 'translateY(-8px)'; e.currentTarget.style.boxShadow = '0 20px 50px rgba(5,150,105,.18),0 1px 0 rgba(255,255,255,.98)'; }}
          style={{
            ...cardBase,
            borderColor: guardianHover ? '#059669' : '#E5E7EB',
            transform: guardianHover ? 'translateY(-8px)' : 'none',
            boxShadow: guardianHover ? '0 20px 50px rgba(5,150,105,.18),0 1px 0 rgba(255,255,255,.98)' : '0 5px 0 #E5E7EB, 0 10px 28px rgba(0,0,0,.06)',
          }}
        >
          <span style={{ fontSize: '3rem', lineHeight: 1, filter: 'drop-shadow(0 4px 8px rgba(0,0,0,.15))', transition: 'transform .15s, filter .15s' }}>👨‍👩‍👧</span>
          <div style={{ fontFamily: "'Sora', sans-serif", fontWeight: 700, fontSize: '1.2rem', color: '#064E3B' }}>
            {T.guardian_title}
          </div>
          <div style={{ fontFamily: "'Nunito', sans-serif", fontSize: '0.9rem', color: '#6B7280' }}>
            {T.guardian_sub}
          </div>
        </div>

        <div
          onClick={() => navigate('/student/auth')}
          onMouseEnter={() => setStudentHover(true)}
          onMouseLeave={() => setStudentHover(false)}
          onMouseDown={e => { e.currentTarget.style.transform = 'translateY(3px)'; e.currentTarget.style.boxShadow = '0 1px 0 #E5E7EB'; }}
          onMouseUp={e => { e.currentTarget.style.transform = 'translateY(-8px)'; e.currentTarget.style.boxShadow = '0 20px 50px rgba(217,119,6,.18),0 1px 0 rgba(255,255,255,.98)'; }}
          style={{
            ...cardBase,
            borderColor: studentHover ? '#D97706' : '#E5E7EB',
            transform: studentHover ? 'translateY(-8px)' : 'none',
            boxShadow: studentHover ? '0 20px 50px rgba(217,119,6,.18),0 1px 0 rgba(255,255,255,.98)' : '0 5px 0 #E5E7EB, 0 10px 28px rgba(0,0,0,.06)',
          }}
        >
          <span style={{ fontSize: '3rem', lineHeight: 1, filter: 'drop-shadow(0 4px 8px rgba(0,0,0,.15))', transition: 'transform .15s, filter .15s' }}>🎓</span>
          <div style={{ fontFamily: "'Sora', sans-serif", fontWeight: 700, fontSize: '1.2rem', color: '#064E3B' }}>
            {T.student_title}
          </div>
          <div style={{ fontFamily: "'Nunito', sans-serif", fontSize: '0.9rem', color: '#6B7280' }}>
            {T.student_sub}
          </div>
        </div>
      </div>

      <a
        href="/jahbath-landing.html"
        style={{
          marginTop: 48,
          color: '#6B7280',
          fontSize: '0.85rem',
          textDecoration: 'none',
          cursor: 'pointer',
        }}
        onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
        onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
      >
        {T.back}
      </a>
    </div>
  );
}

